<template>
  <div class="src-controls flex items-center flex-wrap">
    <vs-button
      class="mr-2 mb-1"
      size="base"
      color="danger"
      type="border"
      icon-pack="feather"
      icon="icon-trash-2"
      @click="showConfirmRemove = true"
    >Remove</vs-button>

    <vs-button
      v-if="videoStreamUID"
      class="mr-2 mb-1"
      size="base"
      color="danger"
      type="filled"
      icon-pack="feather"
      icon="icon-trash-2"
      @click="showConfirmDeleteVideo = true"
    >Delete video</vs-button>

    <vs-dropdown class="cursor-pointer mb-1">
      <vs-button
        size="base"
        color="primary"
        type="border"
        icon-pack="feather"
        icon="icon-tag"
        icon-after
      >Tags{{ associatedTags.length ? ` (${associatedTags.length})` : '' }}</vs-button>
      <vs-dropdown-menu>
        <vs-dropdown-item v-if="tags.length === 0" disabled>
          <span class="text-light">No tags created yet</span>
        </vs-dropdown-item>
        <vs-dropdown-item
          v-for="tag in tags"
          v-if="tag.taggedWith"
          :key="`tw${tag.tag}`"
          class="py-1"
          @click="toggleTag(tag.tag, false)"
        >
          <span>
            <i class="material-icons text-sm inline-block relative icon-down-xs">check_box</i>
            {{ tag.tag }}
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item
          v-for="tag in tags"
          v-if="!tag.taggedWith"
          :key="`ntw${tag.tag}`"
          class="py-1"
          @click="toggleTag(tag.tag, true)"
        >
          <span>
            <i class="material-icons text-sm inline-block relative icon-down-xs">check_box_outline_blank</i>
            {{ tag.tag }}
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item
          v-if="tags.length < 9"
          :divider="tags.length > 0"
          class="py-1"
          @click="optCreateTag"
        >
          <span class="font-bold">+</span> Add Tag
        </vs-dropdown-item>
      </vs-dropdown-menu>
    </vs-dropdown>

    <vs-popup
      class="holamundo"
      title="Remove from showcase"
      :active.sync="showConfirmRemove"
    >
      <p class="text-lg mb-3">Remove this review from your showcase? It will disappear from any widgets it's currently shown on.</p>
      <p class="text-sm text-light mb-4">You can always add it back later from the source platform.</p>
      <div class="overflow-auto">
        <vs-button class="float-right ml-1" color="danger" @click="doRemove">Remove</vs-button>
        <vs-button type="border" class="float-right" @click="showConfirmRemove = false">Cancel</vs-button>
      </div>
    </vs-popup>

    <vs-popup
      class="holamundo"
      title="Permanently delete this video?"
      :active.sync="showConfirmDeleteVideo"
    >
      <p class="text-lg mb-3">This permanently deletes the video file. It will be removed from this response, from your showcase, and from any widgets showing it.</p>
      <p class="text-sm text-light mb-4">This cannot be undone. Any written answer on this response stays.</p>
      <div class="overflow-auto">
        <vs-button class="float-right ml-1" color="danger" :disabled="deletingVideo" @click="doDeleteVideo">{{ deletingVideo ? 'Deleting…' : 'Delete video' }}</vs-button>
        <vs-button type="border" class="float-right" :disabled="deletingVideo" @click="showConfirmDeleteVideo = false">Cancel</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'ShowcasedRowControls',
  props: {
    payload: { type: Object, required: true },
    item: { type: Object, required: true }
  },
  data() {
    return {
      showConfirmRemove: false,
      showConfirmDeleteVideo: false,
      deletingVideo: false
    }
  },
  computed: {
    associatedTags() {
      return (this.item && this.item.taggedWith) || []
    },
    videoStreamUID() {
      const pf = this.payload.data.platform
      if (pf !== 'feedback' && pf !== 'testimonial') return false
      const ans = this.item && this.item.data && this.item.data.answer
      return (ans && typeof ans === 'object' && ans.streamUID) || false
    },
    tags() {
      const toReturn = []
      this.associatedTags.forEach((tag) => {
        toReturn.push({ taggedWith: true, tag })
      })
      this.$store.state.dash.viewData.currentTags.forEach((tag) => {
        if (!toReturn.find(el => el.tag === tag)) {
          toReturn.push({ taggedWith: false, tag })
        }
      })
      return toReturn
    }
  },
  methods: {
    refKey() {
      const pf = this.payload.data.platform
      return (pf === 'feedback' || pf === 'testimonial')
        ? this.payload.data.sessionID
        : this.payload.data.reviewDocID
    },
    doRemove() {
      this.showConfirmRemove = false
      this.payload.method(
        this.payload.data.platform,
        this.payload.data.i,
        this.refKey(),
        false,
        false,
        this.associatedTags
      )
    },
    toggleTag(tag, intent) {
      const trimmed = (tag || '').trim()
      this.payload.method(
        this.payload.data.platform,
        this.payload.data.i,
        this.refKey(),
        trimmed,
        intent,
        this.associatedTags
      )
    },
    optCreateTag() {
      this.$store.commit('dash/TOGGLE_SHOW_CREATE_TAG', (intent, tag = false) => {
        if (!tag) return
        this.toggleTag(tag, intent)
      })
    },
    doDeleteVideo() {
      if (this.deletingVideo) return
      this.deletingVideo = true
      this.$store.dispatch('dash/DELETE_VIDEO_TESTIMONIAL', {
        sessionID: this.payload.data.sessionID,
        jFI: this.payload.data.i,
        streamUID: this.videoStreamUID
      }).then(() => {
        this.deletingVideo = false
        this.showConfirmDeleteVideo = false
        this.$vs.notify({
          title: 'Video deleted',
          text: 'The video has been permanently removed.',
          color: 'success',
          time: 4000
        })
        // Refetch so the (now stripped/removed) item updates in the showcase view.
        this.$store.dispatch('dash/FETCH_SHOWCASED_ITEMS').catch((e) => {
          console.log('refetch after video delete failed', e)
        })
      }).catch(() => {
        this.deletingVideo = false
        this.$vs.notify({
          title: 'Could not delete video',
          text: 'Something went wrong. Please try again, or contact support.',
          color: 'danger',
          time: 4000
        })
      })
    }
  }
}
</script>

<style lang="scss">
// Card rows are narrow on 2xl screens (1/3 grid → ~150-200px per column).
// Without these guards vuesax buttons get squished and the "Remove" label
// clips on both sides. nowrap on the inner text span + flex-shrink:0 on the
// button itself stops the squish; the parent has flex-wrap so the Source
// button drops to the next line if there isn't room.
.src-controls .vs-button {
  flex-shrink: 0;
}
.src-controls .vs-button .vs-button-text {
  white-space: nowrap;
}
</style>
